import React, { useState, useEffect } from 'react';
import { 
  Box,
  Button, 
  Dialog, 
  DialogActions, 
  DialogContent, 
  DialogTitle, 
  Stack,
  Typography,
  useMediaQuery,
  useTheme 
} from '@mui/material';
import { DataGrid, GridActionsCellItem, GridColDef } from '@mui/x-data-grid';
import { Add, Delete, Edit } from '@mui/icons-material';
import { useAdminApi } from '../../api/adminApi';
import { LanguageForm } from '../../forms/LanguageForm';
import { LanguageReq, LanguageResp } from '../../types/language';
import ConfirmationDialog from '../../common/ConfirmationDialog';

const LanguageManager = () => {
  const api = useAdminApi();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [languages, setLanguages] = useState<LanguageResp[]>([]);
  const [openModal, setOpenModal] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [currentLanguage, setCurrentLanguage] = useState<LanguageReq | LanguageResp>({
    name: '',
    code: '',
    isActive: true,
    isRtl: false,
  });
  const [loading, setLoading] = useState(true);

  const fetchLanguages = async () => {
    try {
      setLoading(true);
      const res = await api.listLanguages();
      setLanguages(res.data.data);
    } catch (error) {
      console.error('Diller yüklenirken hata:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setCurrentLanguage({
      name: '',
      code: '',
      isActive: true,
      isRtl: false,
    });
    setOpenModal(true);
  };

  const handleEdit = (language: LanguageResp) => {
    setCurrentLanguage(language);
    setOpenModal(true);
  };

  const handleDelete = (id: number) => {
    setSelectedId(id);
    setOpenDeleteDialog(true);
  };

  const submitForm = async (values: LanguageReq) => {
    try {
      if ('id' in currentLanguage) {
        await api.updateLanguage(currentLanguage.id, values);
      } else {
        await api.createLanguage(values);
      }
      fetchLanguages();
      setOpenModal(false);
    } catch (error) {
      console.error('Dil kaydedilirken hata:', error);
    }
  };

  const confirmDelete = async () => {
    if (selectedId) {
      try {
        await api.deleteLanguage(selectedId);
        fetchLanguages();
      } catch (error) {
        console.error('Dil silinirken hata:', error);
      } finally {
        setOpenDeleteDialog(false);
      }
    }
  };

  useEffect(() => {
    fetchLanguages();
  }, []);

  const columns: GridColDef<LanguageResp>[] = [
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'name', headerName: 'Ad', flex: 1, minWidth: 150 },
    { field: 'code', headerName: 'Kod', width: 100 },
    {
      field: 'isActive',
      headerName: 'Aktif',
      width: 100,
      type: 'boolean',
    },
    {
      field: 'isRtl',
      headerName: 'RTL',
      width: 100,
      type: 'boolean',
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'İşlemler',
      width: isMobile ? 80 : 100,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<Edit />}
          label="Düzenle"
          onClick={() => handleEdit(params.row)}
          showInMenu={isMobile}
        />,
        <GridActionsCellItem
          icon={<Delete />}
          label="Sil"
          onClick={() => handleDelete(params.row.id)}
          showInMenu={isMobile}
        />,
      ],
    },
  ];

  return (
    <Stack spacing={3} sx={{ width: '100%' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
        <Typography variant="h5" component="h2">Diller</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleCreate}
          size={isMobile ? 'small' : 'medium'}
          fullWidth={isMobile}
        >
          Yeni Dil Ekle
        </Button>
      </Stack>

      <Box sx={{ height: 500, width: '100%' }}>
        <DataGrid
          rows={languages}
          columns={columns}
          loading={loading}
          autoHeight
          getRowId={(row) => row.id}
          pageSizeOptions={[5, 10, 25]}
          localeText={{
            noRowsLabel: 'Kayıt bulunamadı',
            footerRowSelected: (count) =>
              `${count.toLocaleString()} satır seçildi`,
          }}
          sx={{
            '& .MuiDataGrid-cell': {
              overflow: 'visible',
              whiteSpace: 'normal',
              wordBreak: 'break-word',
            },
          }}
        />
      </Box>

      <Dialog 
        open={openModal} 
        onClose={() => setOpenModal(false)} 
        maxWidth="sm" 
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>{'id' in currentLanguage ? 'Dili Düzenle' : 'Yeni Dil Ekle'}</DialogTitle>
        <DialogContent>
          <LanguageForm
            initialValues={currentLanguage}
            onSubmit={submitForm}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenModal(false)}>İptal</Button>
        </DialogActions>
      </Dialog>

      <ConfirmationDialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        onConfirm={confirmDelete}
        title="Dili Sil"
        message="Bu dili silmek istediğinize emin misiniz?"
      />
    </Stack>
  );
};

export default LanguageManager;